<template lang="pug">
.wrap
	.container.section
		LayoutBackButton
		ArtistDetailHeader.artist-header(v-if="artistData" :artist="artistData" :songs="response?.results || []" @filter-changed="filterChanged" @sort-filter-changed="sortFilterChanged")
		SongListItemRowWrap.artist-songs(v-if="response?.results && response.results.length > 0 && !onlyGeoblockedSongs" :grid="userStore.getUseSongGrid")
			template(#songs)
				SongListItem(v-for="song in response.results" :layout="userStore.getUseSongGrid ? 'column' : 'listItem'" :key="song.id" :song="song")
				SongRequestButton(:artistName="artistData.name" v-if="!response.nextPage")
		.column.center(v-else-if="filterString && response?.results?.length === 0 && songsStatus !== 'pending'")
			SongNoResults
		.no-songs(v-else-if="onlyGeoblockedSongs && songsStatus !== 'pending'")
			span {{ t('artist.songsNotFound') }}
		.load-more-wrap(ref="loadMoreTrigger" v-if="response?.results && response.results.length > 0")
			SingaButton.is-regular.is-transparent-dark(@click="loadMoreContent()" v-if="!scrollToLoad && response.nextPage && songsStatus !== 'pending'") {{ t('general.loadMore') }}
			CommonLoadingAnimation(v-else-if="scrollToLoad && songsStatus === 'pending'" :timeOut="500")
</template>

<script setup lang="ts">
import { useInfiniteScroll } from '@vueuse/core'
import { storeToRefs } from 'pinia'

const { setMetaTitle, setMetaDescription, setMetaImg } = useMetaTags()

const route = useRoute()
const { hash, slug } = route.params

const { getArtistObject, artistMetaDescription, loadArtistSongs, filterString } = useArtists()

const loadMoreTrigger = ref()
const lengthCheckedTitle = ref('')
const scrollToLoad = ref(false)
const { t } = useI18n()
const userStore = useUserStore()
const page = ref(1)
const sortFilter = ref(route.query.sort?.toString() || 'top')

const geoLocationStore = useGeoLocationStore()
const { contentMarket } = storeToRefs(geoLocationStore)

const defaultSongResult: LoadSongsResult = {
	results: [],
	nextPage: false,
	count: 0,
	page: 1
}

// Fetch artist details
const { data: artistData } = await useAsyncData(
	`artist_detail_${hash}_${slug}_${contentMarket.value?.market}`,
	async () => {
		try {
			return await getArtistObject()
		} catch (error) {
			console.error('Content fetch failed:', error)
			return null
		}
	},
	{
		default: () => null
	}
)

// Then fetch artist songs only if we have artist data
const { data: response, execute: fetchSongs, refresh, status: songsStatus } = await useAsyncData(
	`artist_songs_${artistData.value?.id}_${contentMarket.value.market}_${JSON.stringify({ page: page.value, search: filterString.value, sort: sortFilter.value })}`,
	async () => {
		try {
			if (artistData.value?.id) {
				const result = await loadArtistSongs(artistData.value.id, {
					page: page.value,
					sort: sortFilter.value
				})
				return result
			}
			return defaultSongResult
		} catch (error) {
			console.error('Content fetch failed:', error)
			return defaultSongResult
		}
	},
	{
		immediate: true,
		watch: [page, filterString, sortFilter],
		default: () => defaultSongResult,
		transform: (result: LoadSongsResult): LoadSongsResult => {
			if (!result) return defaultSongResult
			if (page.value === 1) {
				return result
			}
			return {
				...result,
				results: [...(response.value?.results || []), ...result.results]
			}
		}
	}
)

if (import.meta.client) {
	watch(contentMarket, (newVal, oldVal) => {
		if (newVal.market?.toLowerCase() !== oldVal.market?.toLowerCase()) {
			refresh()
		}
	})

	// Watch for artist data changes and route query changes
	watch([artistData, () => route.query.sort], async ([newArtist, newSort]) => {
		if (newArtist?.id) {
			if (newSort && newSort !== sortFilter.value) {
				sortFilter.value = newSort.toString()
			}
			await fetchSongs()
			if (artistData.value) {
				if (!artistData.value.metaDescription) {
					artistData.value.metaDescription = t('artist.meta.description', { artist: artistData.value.name, songs: artistMetaDescription.value })
					const metaDescription = setMetaDescription(artistData.value.metaDescription)
					useHead({
						meta: [...metaDescription]
					})
				}
			}
		}
	}, { immediate: true })
}

if (artistData.value) {
	const pageTitle = artistData.value.name + ' karaoke'
	const metaTitle = setMetaTitle(pageTitle)
	const artistImg = artistData.value?.image?.medium
	const metaImg = setMetaImg(artistImg?.url, artistImg?.width, artistImg?.height)
	const metaDescription = setMetaDescription(artistData.value.metaDescription)

	useHead({
		title: pageTitle,
		meta: [
			...metaTitle,
			...metaDescription,
			...metaImg
		]
	})
}

const onlyGeoblockedSongs = computed(() => {
	return response.value?.results.every((song: Song) => !song.available_markets.includes(contentMarket.value.market as string))
})

const filterChanged = async (string: string) => {
	if (!artistData.value) { return }
	filterString.value = string
	page.value = 1
	scrollToLoad.value = false
	lengthCheckedTitle.value = useTruncate(string)
}

const sortFilterChanged = async (sortFilter: any) => {
	if (!artistData.value) { return }
	await navigateTo({ path: route.path, query: { ...route.query, sort: sortFilter.id }, replace: true })
	page.value = 1
	scrollToLoad.value = false
	sortFilter.value = sortFilter.id
}

useInfiniteScroll(
	loadMoreTrigger,
	async () => {
		if (response.value?.nextPage && songsStatus.value !== 'pending' && scrollToLoad.value && artistData.value?.id) {
			page.value++
		}
	},
	{ distance: 10, interval: 1000 }
)

const loadMoreContent = async () => {
	if (artistData.value?.id) {
		scrollToLoad.value = true
		page.value++
	}
}
</script>

<style lang="sass" scoped>
.back-button
	margin-top: $spacing-24
.artist-header
	margin-top: $spacing-16
	@media (min-width: $tablet)
		margin-top: $spacing-32
.artist-songs
	margin: $spacing-24 0
.load-more-wrap
	text-align: center
	margin: $spacing-16 0 $spacing-32

.center
	text-align: center
	margin-top: $spacing-24

.no-songs
	text-align: center
	margin-top: $spacing-64
</style>
